import React from 'react';
import { IconButton } from './Button';
import { Icon } from '../icons';
import { useT } from '../../i18n';

/**
 * PageHeader — the standard top-of-page chrome: optional breadcrumbs, a title
 * (with optional back button / icon / meta), a description, and right-aligned
 * actions. A `tabs` slot renders below the title row.
 *
 * Props:
 *   title, description: ReactNode
 *   breadcrumbs: ReactNode        — e.g. <Breadcrumbs .../>
 *   actions: ReactNode            — right-aligned controls
 *   meta: ReactNode               — inline next to the title (e.g. a Badge)
 *   icon: ReactNode
 *   onBack: () => void            — renders a back button before the title
 *   tabs: ReactNode               — e.g. <Tabs .../>
 *   sticky: boolean
 */
const PageHeader = ({
  title,
  description,
  breadcrumbs,
  actions,
  meta,
  icon,
  onBack,
  tabs,
  sticky = false,
  className = '',
}) => {
  const t = useT();
  return (
    <header className={`ps-pageheader${sticky ? ' ps-pageheader--sticky' : ''} ${className}`.trim()}>
      {breadcrumbs && <div className="ps-pageheader__crumbs">{breadcrumbs}</div>}
      <div className="ps-pageheader__row">
        <div className="ps-pageheader__lead">
          {onBack && (
            <IconButton aria-label={t('Go back')} variant="ghost" size="icon-sm" className="ps-pageheader__back" onClick={onBack}>
              <Icon.ArrowLeft />
            </IconButton>
          )}
          {icon && <span className="ps-pageheader__icon">{icon}</span>}
          <div className="ps-pageheader__headings">
            <div className="ps-pageheader__titlerow">
              <h1 className="ps-pageheader__title">{title}</h1>
              {meta && <span className="ps-pageheader__meta">{meta}</span>}
            </div>
            {description && <p className="ps-pageheader__desc">{description}</p>}
          </div>
        </div>
        {actions && <div className="ps-pageheader__actions">{actions}</div>}
      </div>
      {tabs && <div className="ps-pageheader__tabs">{tabs}</div>}
    </header>
  );
};

export default PageHeader;
